// RITA-1597 — Beispielfälle der Vorschau „Beispielwerte“ im Vertragseditor.
//
// Wer eine Vorlage schreibt, soll sehen, was später wirklich im Vertrag steht. Vier Fälle zeigen, wie sich
// Anrede, Name, Geschlecht, Persönliche Anrede, Vertragsinhaber und Vertragspartner ändern: Berater,
// Beraterin, Abweichender Vertragsinhaber: Frau und Abweichender Vertragsinhaber: Firma (Bezeichnungen wie das Feld
// beim Berater – Entscheidung Julia 03.11.2026, RITA-1599). {{FIRMA}} ist in jedem
// Fall unsere Marke (die Marke aus der Zuordnung der Vorlage, sonst `BEISPIEL_MARKE`). Zu jedem Fall gehört
// der Satz, für welche „Für“-Auswahl das PRM Vertrag und Anschreiben sucht – nach der Regel der
// Vertragserstellung (Backend `contractTemplateSelection`): der Vertrag richtet sich nach der Art des
// abweichenden Vertragsinhabers (RITA-1560), das Anschreiben immer nach dem Berater; „Firma als
// Vertragspartner“ passt nur zu Firma, sonst gilt die genauere Vorlage vor „Berater/in – automatisch“.
// Die Auswahl ändert nur die Ansicht und die PDF-Probe; beim Öffnen gilt immer der Fall „Berater“.
import { VORLAGEN_GESCHLECHTER } from '../../services/vorlagenZuordnung';
import { BEISPIEL_MARKE, VERTRAG_BEISPIELWERTE, VertragPlatzhalterKey } from './platzhalterkatalog';

export type Beispielfall = 'berater' | 'beraterin' | 'vertragsinhaberin' | 'firma';

/** Beispielfall und Marke, mit denen Seitenansicht und PDF-Probe die Beispielwerte zeigen. */
export interface Beispielauswahl {
  fall: Beispielfall;
  /** Titel der Marke aus der Zuordnung der Vorlage; `null` = keine gewählt, dann `BEISPIEL_MARKE`. */
  marke: string | null;
}

type VorlagenFuer = keyof typeof VORLAGEN_GESCHLECHTER;

export interface BeispielfallEintrag {
  wert: Beispielfall;
  bezeichnung: string;
  /** „Für“ der Vertragsvorlage, die das PRM zuerst sucht; nur `company` ohne Rückfall auf `both`. */
  vertragFuer: Exclude<VorlagenFuer, 'both'>;
  /** „Für“ des Anschreibens: immer der Berater selbst. */
  anschreibenFuer: Exclude<VorlagenFuer, 'both' | 'company'>;
  werte: Partial<Record<VertragPlatzhalterKey, string>>;
}

const BERATERIN: Partial<Record<VertragPlatzhalterKey, string>> = {
  ANREDE: 'Frau',
  VORNAME: 'Anna',
  NACHNAME: 'Musterfrau',
  GESCHLECHT: 'Weiblich',
  PERSOENLICHE_ANREDE: 'Liebe Anna',
  VERTRAGSPARTNER: 'Frau Anna Musterfrau',
  GESCHAEFTSEMAIL: 'anna.musterfrau@example.de',
  PRIVATEEMAIL: 'anna.privat@example.de',
  MYJACK_LOGIN: 'amusterfrau',
};

/**
 * Die vier Fälle in der Reihenfolge der Auswahl; der erste ist der Grundfall. Die Bezeichnungen bleiben kurz, damit
 * die Auswahl auch auf einem Laptop (1440 px, Menü offen) neben den vier Knöpfen und vor „Seiten“ Platz hat; die
 * Personen (Herr Max Mustermann, Frau Anna Musterfrau, Frau Erika Mustermann, Firma Muster Reisen GmbH) zeigt die
 * Vorschau selbst.
 */
export const BEISPIELFAELLE: readonly BeispielfallEintrag[] = [
  { wert: 'berater', bezeichnung: 'Berater', vertragFuer: 'male', anschreibenFuer: 'male', werte: {} },
  { wert: 'beraterin', bezeichnung: 'Beraterin', vertragFuer: 'female', anschreibenFuer: 'female', werte: BERATERIN },
  {
    wert: 'vertragsinhaberin',
    bezeichnung: 'Abweichender Vertragsinhaber: Frau',
    vertragFuer: 'female',
    anschreibenFuer: 'male',
    werte: { VERTRAGSINHABER: 'Frau Erika Mustermann', VERTRAGSPARTNER: 'Frau Erika Mustermann' },
  },
  {
    wert: 'firma',
    bezeichnung: 'Abweichender Vertragsinhaber: Firma',
    vertragFuer: 'company',
    anschreibenFuer: 'male',
    werte: { VERTRAGSINHABER: 'Firma Muster Reisen GmbH', VERTRAGSPARTNER: 'Firma Muster Reisen GmbH' },
  },
];

export const STANDARD_BEISPIELFALL: Beispielfall = BEISPIELFAELLE[0].wert;

export function beispielfallEintrag(fall: Beispielfall): BeispielfallEintrag {
  return BEISPIELFAELLE.find((eintrag) => eintrag.wert === fall) ?? BEISPIELFAELLE[0];
}

/** Beispielwerte aller Vertragsplatzhalter für einen Fall; `marke` füllt {{FIRMA}}. */
export function beispielwerte(
  fall: Beispielfall,
  marke: string | null = null,
): Readonly<Record<VertragPlatzhalterKey, string>> {
  return { ...VERTRAG_BEISPIELWERTE, ...beispielfallEintrag(fall).werte, FIRMA: marke ?? BEISPIEL_MARKE };
}

/** Der Satz unter der Auswahl: welche Vorlagen das PRM in diesem Fall nimmt. */
export function vorlagenwahlSatz(fall: Beispielfall): string {
  const eintrag = beispielfallEintrag(fall);
  const vertrag =
    eintrag.vertragFuer === 'company'
      ? `Vertrag nur für „${VORLAGEN_GESCHLECHTER.company}“`
      : `Vertrag für „${VORLAGEN_GESCHLECHTER[eintrag.vertragFuer]}“`;
  const anschreiben = `Anschreiben für „${VORLAGEN_GESCHLECHTER[eintrag.anschreibenFuer]}“`;
  return `Das PRM nimmt in diesem Fall: ${vertrag}, ${anschreiben}, sonst „${VORLAGEN_GESCHLECHTER.both}“.`;
}
